import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Play, ArrowRight } from "lucide-react";
import { IMAGES, waLink } from "../../lib/site";
import { scrollToId } from "../../lib/scroll";
import { EASE } from "./Reveal";

const LINES = ["Where Passion", "Meets the", "Classics."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const vinylY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section id="home" ref={ref} className="relative overflow-hidden pt-[68px]" data-testid="hero-section">
      <div className="spotlight-gold pointer-events-none absolute -top-40 right-[-10%] h-[560px] w-[560px]" />
      <div className="spotlight-burgundy pointer-events-none absolute bottom-[-20%] left-[-12%] h-[520px] w-[520px]" />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-10 sm:px-6 lg:grid-cols-12 lg:items-center lg:pb-24 lg:pt-16">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/5 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-gold"
            data-testid="hero-eyebrow"
          >
            Karaoke and Live Music · Chandigarh Tricity
          </motion.p>
          <h1 className="mt-6 font-serif text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic text-gold" : "text-ivory"}`}
                  initial={{ y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.95, delay: 0.25 + i * 0.16, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300"
          >
            Tricity Kroonerz — a talented ensemble of singers bringing retro Bollywood and English
            classics to celebrations, corporate events, cafés, societies and concerts across
            Chandigarh Tricity.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0, ease: EASE }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <button
              onClick={() => scrollToId("#book")}
              data-testid="hero-book-button"
              className="group inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-navy-950 transition-all hover:bg-gold-light"
            >
              Book Tricity Kroonerz
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToId("#videos")}
              data-testid="hero-watch-button"
              className="inline-flex items-center gap-2 rounded-full border border-navy-700 bg-navy-850/60 px-6 py-3.5 text-sm font-semibold text-ivory transition-colors hover:border-gold/50 hover:text-gold"
            >
              <Play className="h-4 w-4" /> Watch Us Perform
            </button>
            <a
              href={waLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex animate-pulse-gold items-center gap-2 rounded-full bg-[#1f9e4d] px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#25b558]"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp Us
            </a>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-8 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500"
          >
            Chandigarh · Mohali · Panchkula · Zirakpur · Kharar · New Chandigarh
          </motion.p>
        </motion.div>

        <motion.div style={{ y: vinylY }} className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            className="relative mx-auto max-w-md"
          >
            <div className="gold-frame relative overflow-hidden rounded-2xl border border-gold/25">
              <img
                src={IMAGES.hero}
                alt="Vintage microphone under warm stage spotlight"
                className="h-[420px] w-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/85 via-transparent to-navy-950/20" />
              <div className="absolute bottom-5 left-5 right-24 sm:right-32">
                <p className="font-serif text-lg italic text-gold-light">"They retired from their careers —</p>
                <p className="font-serif text-lg italic text-ivory">not from their passion for music."</p>
              </div>
            </div>
            <div className="vinyl absolute -bottom-10 -right-6 h-36 w-36 animate-spin-slow sm:h-44 sm:w-44" data-testid="hero-vinyl">
              <div className="absolute inset-0 m-auto flex h-14 w-14 items-center justify-center rounded-full bg-burgundy sm:h-16 sm:w-16">
                <span className="font-serif text-lg font-bold text-gold-light">TK</span>
              </div>
              <div className="absolute inset-0 m-auto h-2 w-2 rounded-full bg-ivory/80" />
            </div>
            <div className="absolute -left-5 top-8 animate-floaty rounded-xl border border-navy-700 bg-navy-900/90 px-4 py-3 backdrop-blur" data-testid="hero-voices-card">
              <p className="font-serif text-2xl font-bold text-gold">~16</p>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Voices Strong</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
